import React, { useState, useMemo, useCallback } from 'react';
import {
  X as LucideX,
  ArrowLeft as LucideArrowLeft,
  Search as LucideSearch,
  BookOpen as LucideBookOpen,
  Eye as LucideEye,
  EyeOff as LucideEyeOff,
  AlertTriangle as LucideAlertTriangle,
  User as LucideUser,
  MapPin as LucideMapPin,
  KeyRound as LucideKeyRound,
  FileText as LucideFileText,
  Zap as LucideZap,
  CircleHelp as LucideCircleHelp
} from 'lucide-react';
import type { ClueDefinition, ClueCategory, ClueWindowState } from 'shared';
import { CLUE_CATEGORIES, groupCluesByCategory, markClueAsRead, getUnreadClueCount } from 'shared';

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const X = LucideX as unknown as IconComponent;
const ArrowLeft = LucideArrowLeft as unknown as IconComponent;
const Search = LucideSearch as unknown as IconComponent;
const BookOpen = LucideBookOpen as unknown as IconComponent;
const Eye = LucideEye as unknown as IconComponent;
const EyeOff = LucideEyeOff as unknown as IconComponent;
const AlertTriangle = LucideAlertTriangle as unknown as IconComponent;

/* P8-2a（P7-5 の適用漏れ）: カテゴリの絵文字を lucide へ統一する表示層マッピング。
   カテゴリ定義の SSoT は packages/shared/src/clueSchema.ts の CLUE_CATEGORIES
   （凍結スキーマ・変更禁止）のまま。ここは Player 側の「絵文字→lucide」写像のみを持つ。
   対応: 👤person→User / 📍location→MapPin / 🔑item→KeyRound /
        📝information→FileText / ⚡event→Zap / ❓mystery→CircleHelp */
const CLUE_CATEGORY_ICONS: Record<ClueCategory, IconComponent> = {
  person: LucideUser as unknown as IconComponent,
  location: LucideMapPin as unknown as IconComponent,
  item: LucideKeyRound as unknown as IconComponent,
  information: LucideFileText as unknown as IconComponent,
  event: LucideZap as unknown as IconComponent,
  mystery: LucideCircleHelp as unknown as IconComponent,
};

function ClueCategoryIcon({ category, size }: { category: ClueCategory; size: number }) {
  // 未知カテゴリは旧 '📝' フォールバックと同義の FileText に落とす（絵文字は出さない）。
  const IconComp = CLUE_CATEGORY_ICONS[category] ?? (LucideFileText as unknown as IconComponent);
  return <IconComp width={size} height={size} className="flex-shrink-0" aria-hidden="true" />;
}

interface ClueWindowProps {
  /** 手がかり定義配列 */
  clueDefinitions: ClueDefinition[];
  /** 手がかり状態 */
  clueState: ClueWindowState;
  /** 状態更新コールバック */
  onStateChange: (state: ClueWindowState) => void;
  /** 閉じるコールバック */
  onClose: () => void;
  /** 表示中か */
  isOpen: boolean;
}

/**
 * 手がかりウィンドウコンポーネント
 * プレイ中にこれまでに得た手がかりを確認できるサイドパネル
 */
export const ClueWindow: React.FC<ClueWindowProps> = ({
  clueDefinitions,
  clueState,
  onStateChange,
  onClose,
  isOpen,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<ClueCategory | 'all'>('all');
  const [searchText, setSearchText] = useState('');
  const [selectedClue, setSelectedClue] = useState<ClueDefinition | null>(null);
  const [showSpoilers, setShowSpoilers] = useState(false);

  // カテゴリ別グループ化
  const groupedClues = useMemo(() => {
    return groupCluesByCategory(clueDefinitions, clueState);
  }, [clueDefinitions, clueState]);

  // フィルタリングされた手がかり
  const filteredClues = useMemo(() => {
    let clues: { definition: ClueDefinition; state: { unlocked: boolean; read: boolean } }[] = [];

    if (selectedCategory === 'all') {
      Object.values(groupedClues).forEach((categoryClues) => {
        clues = clues.concat(categoryClues);
      });
    } else {
      clues = groupedClues[selectedCategory] || [];
    }

    // 検索フィルター
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      clues = clues.filter(
        (c) =>
          c.definition.title.toLowerCase().includes(search) ||
          c.definition.description.toLowerCase().includes(search)
      );
    }

    // スポイラーフィルター
    if (!showSpoilers) {
      clues = clues.filter((c) => !c.definition.isSpoiler);
    }

    // 重要度でソート（高い順）
    return clues.sort((a, b) => b.definition.importance - a.definition.importance);
  }, [groupedClues, selectedCategory, searchText, showSpoilers]);

  // 未読数
  const unreadCount = useMemo(() => getUnreadClueCount(clueState), [clueState]);

  // 手がかり選択
  const handleClueSelect = useCallback(
    (clue: ClueDefinition) => {
      setSelectedClue(clue);
      // 既読にする
      if (!clueState.clues[clue.id]?.read) {
        onStateChange(markClueAsRead(clueState, clue.id));
      }
    },
    [clueState, onStateChange]
  );

  const handleCategorySelect = useCallback((category: ClueCategory | 'all') => {
    setSelectedCategory(category);
    setSelectedClue(null);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setSelectedClue(null);
  }, []);

  const handleSpoilerToggle = useCallback(() => {
    setShowSpoilers((value) => !value);
    setSelectedClue(null);
  }, []);

  // カテゴリ別のカウント
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };
    Object.entries(groupedClues).forEach(([category, clues]) => {
      counts[category] = clues.length;
      counts.all += clues.length;
    });
    return counts;
  }, [groupedClues]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      {/* P8-2b: slate/amber の Tailwind 色ユーティリティを廃し、09-hud-windows.css の
          骨董トークン参照の意味クラスへ（レイアウト util・構造・文言は不変）。 */}
      <div className="clue-window-panel w-full max-w-4xl h-[80vh] rounded-xl flex flex-col overflow-hidden">
        {/* ヘッダー */}
        <div className="clue-window-header flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <BookOpen className="clue-window-title-icon w-5 h-5" />
            <h2 className="text-lg font-bold">手がかり</h2>
            {unreadCount > 0 && (
              <span className="clue-unread-badge px-2 py-0.5 text-xs rounded-full font-bold">
                {unreadCount} 未読
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="手がかりを閉じる"
            className="clue-window-close-btn p-1 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="clue-window-body flex flex-1 overflow-hidden">
          {/* サイドバー: カテゴリ */}
          <div className="clue-window-sidebar w-48 p-2 flex flex-col">
            <button
              onClick={() => handleCategorySelect('all')}
              className={`clue-cat-btn flex items-center justify-between px-3 py-2 rounded-lg mb-1 transition-colors ${
                selectedCategory === 'all' ? 'is-active' : ''
              }`}
            >
              <span>すべて</span>
              <span className="text-xs opacity-60">{categoryCounts.all}</span>
            </button>

            {Object.entries(CLUE_CATEGORIES).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => handleCategorySelect(key as ClueCategory)}
                className={`clue-cat-btn flex items-center justify-between px-3 py-2 rounded-lg mb-1 transition-colors ${
                  selectedCategory === key ? 'is-active' : ''
                }`}
              >
                <span className="flex items-center gap-2">
                  <ClueCategoryIcon category={key as ClueCategory} size={16} />
                  <span>{cat.name}</span>
                </span>
                <span className="text-xs opacity-60">{categoryCounts[key] || 0}</span>
              </button>
            ))}

            {/* スポイラー表示切り替え */}
            <div className="clue-sidebar-footer mt-auto pt-2">
              <button
                onClick={handleSpoilerToggle}
                className={`clue-spoiler-toggle flex items-center gap-2 px-3 py-2 w-full rounded-lg transition-colors ${
                  showSpoilers ? 'is-on' : ''
                }`}
              >
                {showSpoilers ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                <span className="text-sm">ネタバレ表示</span>
              </button>
            </div>
          </div>

          {/* メインエリア */}
          <div className="clue-window-main flex-1 flex flex-col overflow-hidden">
            {/* 検索バー */}
            <div className="clue-search-bar p-3">
              <div className="relative">
                <Search className="clue-search-icon absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" />
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="手がかりを検索"
                  className="clue-search-input w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none"
                />
              </div>
            </div>

            {/* 手がかりリスト */}
            <div className="flex-1 overflow-y-auto p-3">
              {filteredClues.length === 0 ? (
                <div className="clue-empty flex flex-col items-center justify-center h-full">
                  <BookOpen className="w-12 h-12 mb-2 opacity-50" />
                  <p>まだ手がかりがありません</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {filteredClues.map(({ definition, state }) => (
                    <button
                      key={definition.id}
                      onClick={() => handleClueSelect(definition)}
                      className={`clue-item text-left p-3 rounded-lg border transition-all ${
                        selectedClue?.id === definition.id ? 'is-selected' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="clue-cat-icon text-lg flex items-center">
                          <ClueCategoryIcon category={definition.category} size={18} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className={`clue-item-title font-medium truncate ${
                              state.read ? 'is-read' : ''
                            }`}>
                              {definition.title}
                            </h4>
                            {!state.read && (
                              <span className="clue-unread-dot flex-shrink-0 w-2 h-2 rounded-full" />
                            )}
                            {definition.isSpoiler && (
                              <AlertTriangle className="clue-spoiler-icon flex-shrink-0 w-3 h-3" />
                            )}
                          </div>
                          <p className="clue-item-desc text-sm truncate">
                            {definition.description}
                          </p>
                          {definition.importance === 3 && (
                            <span className="clue-important-chip inline-block mt-1 px-1.5 py-0.5 text-xs rounded">
                              重要
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 詳細パネル */}
          {selectedClue && (
            <div className="clue-detail-panel w-72 p-4 overflow-y-auto">
              <div className="clue-detail-panel__actions">
                <button
                  type="button"
                  className="clue-detail-back-btn"
                  onClick={() => setSelectedClue(null)}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>一覧へ戻る</span>
                </button>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <span className="clue-cat-icon text-2xl flex items-center">
                  <ClueCategoryIcon category={selectedClue.category} size={24} />
                </span>
                <div>
                  <h3 className="clue-detail-title font-bold">{selectedClue.title}</h3>
                  <span className="clue-detail-cat text-xs">
                    {CLUE_CATEGORIES[selectedClue.category]?.name}
                  </span>
                </div>
              </div>

              {selectedClue.imageUrl && (
                <img
                  src={selectedClue.imageUrl}
                  alt={selectedClue.title}
                  className="w-full h-32 object-cover rounded-lg mb-3"
                />
              )}

              <p className="clue-detail-desc text-sm mb-4">{selectedClue.description}</p>

              {selectedClue.discoveredAt && (
                <div className="clue-detail-found text-xs mb-2">
                  <span>発見場所:</span> {selectedClue.discoveredAt}
                </div>
              )}

              {selectedClue.importance === 3 && (
                <div className="clue-important-chip px-2 py-1 text-xs rounded mb-2">
                  重要な手がかり
                </div>
              )}

              {selectedClue.isSpoiler && (
                <div className="clue-spoiler-chip flex items-center gap-1 px-2 py-1 text-xs rounded">
                  <AlertTriangle className="w-3 h-3" />
                  核心に近い情報
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClueWindow;
